import React from 'react';
import { Upload, Button, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import RestHelper from 'utils/RestHelper';
import { ze } from 'utils/zhEn';
import { CS_STYLES } from 'utils/constants/style-constants';

// 支持的文件扩展名：TXT, MD, PDF, HTML, XLSX, XLS, DOCX, CSV, EML, MSG, PPTX, PPT, XML, EPUB, LOG, JSON
// 必须与后端 DifyFileUploadService.uploadSelfAttach 的扩展名白名单保持一致（后端另兼容 markdown）。
const difySupportedExtensionList = ['txt', 'md', 'pdf', 'html', 'xlsx', 'xls', 'docx', 'csv', 'eml', 'msg', 'pptx', 'ppt', 'xml', 'epub'];
const extraSupportedExtensionList = ['log', 'json'];
// 去重：xml / csv 同时存在于上面两个列表，直接 concat 会让提示文案和 accept 属性出现重复项
const supportedFileExtensionList = [...new Set([...difySupportedExtensionList, ...extraSupportedExtensionList])];

// 显示给用户看的支持类型
const supportedTypesDescText = supportedFileExtensionList.map(ext => `${ext}`).join(', ');
// 生成accept属性，用于限制文件选择对话框
const acceptTypes = supportedFileExtensionList.map(ext => `.${ext}`).join(',');

/**
 * @param {number} [maxFiles=10] 本组件与「上报附件」合计允许的最大文件数（CrashSight 自有约束）
 * @param {number} [usedCount=0] 已被「上报附件」占用的数量
 */
function AiIssueAnalysisFileUpload({ fileList = [], onFileListChange, appId, issueId, maxFiles = 10, usedCount = 0 }) {
  // 剩余可选数量：名额被「上报附件已选数 + 本组件已上传数」共同占用
  const remainingSlots = maxFiles - usedCount - fileList.length;
  const isFull = remainingSlots <= 0;

  async function handleCustomRequest(options) {
    const { file, onSuccess, onError, onProgress } = options;

    try {
      // 模拟上传进度
      onProgress({ percent: 10 });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('filename', file.name);
      formData.append('appId', appId);
      formData.append('issueId', issueId);

      onProgress({ percent: 50 });

      const rsp = await RestHelper.mustPost('/api/aiPilot/uploadSelfAttach', formData);

      onProgress({ percent: 100 });

      if (rsp.json.data) {
        onSuccess(rsp.json.data, file);
        message.success(ze('文件上传成功', 'File uploaded successfully'));
      } else {
        throw new Error(rsp?.json?.message || ze('上传失败', 'Upload failed'));
      }
    } catch (error) {
      console.error('Upload error:', error);

      // 上传失败的文件保留在列表里（antd 会标红），由用户手动移除。
      // 失败即自动剔除会让文件数回落，导致「已达上限」提示消失、按钮又变回可点。
      onError(error);
      message.error(ze('文件上传失败', 'File upload failed'));
    }
  }

  function handleChange(info) {
    const { fileList: newFileList } = info;

    // 更新文件列表状态
    if (onFileListChange) {
      onFileListChange(newFileList);
    }
  }

  function beforeUpload(file, batchFiles) {
    // 检查文件类型 - 只允许支持的文件格式
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();
    const isSupportedFile = supportedFileExtensionList.includes(fileExtension);

    if (!isSupportedFile) {
      const supportedFormats = supportedFileExtensionList.map(ext => ext.toUpperCase()).join(', ');
      message.error(ze(
        `只支持以下文件格式：${supportedFormats}`,
        `Only the following file formats are supported: ${supportedFormats}`
      ));
      return Upload.LIST_IGNORE;
    }

    // 检查文件大小 (10MB)
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error(ze('文件大小不能超过10MB', 'File size cannot exceed 10MB'));
      return Upload.LIST_IGNORE;
    }

    // 检查文件数量 - 名额不足直接拦截，不再靠「上传失败后剔除」来纠正数量。
    // 多选时 beforeUpload 对批次内每个文件各调一次，用批次内序号分配剩余名额。
    const indexInBatch = Array.isArray(batchFiles) ? batchFiles.indexOf(file) : 0;
    if (remainingSlots <= indexInBatch) {
      message.error(ze(`最多只能选择${maxFiles}个文件（含上报附件）`, `You can only select up to ${maxFiles} files (including report attachments)`));
      return Upload.LIST_IGNORE;
    }

    return true;
  }

  const uploadProps = {
    fileList,
    onChange: handleChange,
    customRequest: handleCustomRequest,
    beforeUpload,
    multiple: true,
    accept: acceptTypes,
    // 名额用尽时禁掉文件选择：openFileDialogOnClick 是 antd 4 明确支持的开关，
    // 只禁 Button 的话点按钮周围空白仍会弹框。这里刻意不给 Upload 传 disabled——
    // antd 4 下它可能连带禁用文件列表的移除图标，那样「选满且有一个失败」就既删不掉也提交不了。
    openFileDialogOnClick: !isFull,
    showUploadList: {
      showRemoveIcon: true,
      showDownloadIcon: false,
    },
  };

  return <div>
    <div style={{ marginBottom: '4px', fontSize: '12px', color: CS_STYLES.SUB_TEXT_COLOR }}>{ ze('支持的文件类型：', 'Supported formats: ') } { supportedTypesDescText }</div>
    <Upload {...uploadProps}>
      <Button icon={<UploadOutlined />} disabled={isFull}>
        {ze('选择文件', 'Select Files')}
      </Button>
    </Upload>
  </div>;
}

export default AiIssueAnalysisFileUpload;
